/**
 * CablingDesigner — lienzo único de cableado físico del CMDB.
 *
 * Misma mecánica que el Diseñador de Switches (clic en un puerto libre → clic
 * en otro → modal del cable), pero con TODOS los extremos cableables:
 *
 *   switch · patch panel · toma de pared (2 caras) · servidor/cabina ·
 *   PC · impresora · teléfono IP · AP WiFi
 *
 * Las aristas salen del grafo del backend (`GET /api/cabling/graph`), que
 * unifica `PortConnection`, el cable de pared de las tomas (FK) y los enlaces
 * de backbone entre armarios (`backbone_links`). Aquí no se decide nada de
 * negocio: se pinta lo que llega y se manda cablear al endpoint `/cabling/link`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowsRightLeftIcon,
  BuildingOffice2Icon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleStackIcon,
  ComputerDesktopIcon,
  EyeSlashIcon,
  GlobeAltIcon,
  LinkIcon,
  PhoneIcon,
  PrinterIcon,
  QueueListIcon,
  ServerIcon,
  ShieldCheckIcon,
  EyeIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import LocationFilter, { matchesLocation } from './LocationFilter';
import PortElementForm from './PortElementForm';
import {
  CABLE_COLORS,
  CABLE_TYPES,
  PORT_H,
  PORT_W,
  ZMAX,
  ZMIN,
  cableRoute,
  clamp,
  colorHex,
  exitSide,
  isFiber,
  polylineMid,
  portGrid,
  portOffset,
  roundedPath,
  truncate,
} from './wiring/wiringGeometry';
import {
  RACK_HEADER,
  U_H,
  buildRackLayout,
  rackCablePoints,
  rackExitSide,
  rackPortOffset,
} from './wiring/rackGeometry';
import { tracePath } from './wiring/cablePath';
import './SwitchWiringCanvas.css';
import './CablingDesigner.css';

// Último cable usado: al conectar otra vez lo normal es repetir tipo, color y
// medida (se racha con 1,5 m de Cat6A y no se quiere reescribir cada vez).
const LAST_CABLE_KEY = 'cmdb.cbd.lastCable';
const readLastCable = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(LAST_CABLE_KEY) || 'null');
    return raw && typeof raw === 'object' ? raw : null;
  } catch { return null; }
};
const saveLastCable = (values) => {
  try { localStorage.setItem(LAST_CABLE_KEY, JSON.stringify(values)); } catch { /* noop */ }
};

// Iconografía del proyecto: @heroicons/react/24/outline (nada de emojis).
const KIND_META = {
  switch: { icon: ArrowsRightLeftIcon, label: 'Switch', color: '#3b82f6', order: 0, card: 'ports' },
  panel: { icon: QueueListIcon, label: 'Patch panel', color: '#8b5cf6', order: 1, card: 'ports' },
  router: { icon: GlobeAltIcon, label: 'Router', color: '#6366f1', order: 2, card: 'ports' },
  firewall: { icon: ShieldCheckIcon, label: 'Firewall', color: '#ef4444', order: 3, card: 'ports' },
  server: { icon: ServerIcon, label: 'Servidor', color: '#0ea5e9', order: 4, card: 'ports' },
  storage: { icon: CircleStackIcon, label: 'Cabina', color: '#14b8a6', order: 5, card: 'ports' },
  outlet: { icon: BuildingOffice2Icon, label: 'Toma', color: '#22c55e', order: 6, card: 'compact' },
  workstation: { icon: ComputerDesktopIcon, label: 'Equipo', color: '#06b6d4', order: 7, card: 'compact' },
  printer: { icon: PrinterIcon, label: 'Impresora', color: '#f97316', order: 8, card: 'compact' },
  ip_phone: { icon: PhoneIcon, label: 'Teléfono', color: '#eab308', order: 9, card: 'compact' },
  wifi_ap: { icon: WifiIcon, label: 'AP WiFi', color: '#10b981', order: 10, card: 'compact' },
};

/** Icono del tipo de nodo (componente Heroicons). */
const KindIcon = ({ kind, ...props }) => {
  const Ico = KIND_META[kind]?.icon || LinkIcon;
  return <Ico {...props} />;
};

const ALL_KINDS = Object.keys(KIND_META);

const EDGE_META = {
  port_connection: { label: 'Latiguillo', width: 2.2, dash: null },
  backbone: { label: 'Enlace entre armarios', width: 5, dash: null },
  wall_run: { label: 'Cable de pared', width: 2, dash: '2 4' },
};

const GROUP_PAD = 30;
const GROUP_HEADER = 26;
const GROUP_GAP_X = 130;
const GROUP_GAP_Y = 140;
const COMPACT_W = 104;

const SCOPES = { company: 'Toda la empresa', location: 'Sede', racks: 'Armarios' };

// ─────────────────────────────────────────────────────────────────────────────
// Geometría
// ─────────────────────────────────────────────────────────────────────────────

/** Tarjeta compacta (tomas y equipos): pocos puertos y mucho menos alto. */
function compactGrid(portCount) {
  const count = Math.max(1, portCount || 1);
  const cols = count <= 4 ? 1 : 2;
  const rows = Math.ceil(count / cols);
  const gridW = cols * PORT_W + (cols - 1) * 8;
  const width = COMPACT_W + gridW + 10;
  const entryY = 9;
  const rowH = 16;
  const height = Math.max(34, entryY + rows * rowH + 6);
  return {
    width,
    height,
    cols,
    rows,
    rowSizes: Array.from({ length: rows }, (_, r) => Math.min(cols, count - r * cols)),
    numbering: 'rows',
    padX: COMPACT_W,
    rowH,
    entryY,
    infoW: COMPACT_W - 14,
    compact: true,
  };
}

function compactOffset(geo, index) {
  const col = index % geo.cols;
  const row = Math.floor(index / geo.cols);
  return {
    x: geo.padX + 5 + col * (PORT_W + 8) + PORT_W / 2,
    y: geo.entryY + row * geo.rowH + PORT_H / 2,
  };
}

const geoOfNode = (node) => (
  KIND_META[node.kind]?.card === 'compact'
    ? compactGrid((node.ports || []).length)
    // Filas y numeración del panel tal y como se configuraron en el
    // Diseñador de switches (las dos vistas pintan igual el mismo switch).
    : portGrid(
      (node.ports || []).length,
      node.meta?.canvas_rows,
      node.meta?.canvas_numbering,
    )
);

const offsetOf = (geo, index) => {
  if (geo.mode === 'rack') return rackPortOffset(geo, index);
  return geo.compact ? compactOffset(geo, index) : portOffset(geo, index);
};

function sideOf(geo, index, selfY, otherY) {
  if (geo.mode === 'rack') return rackExitSide(geo, index, selfY, otherY);
  // Las compactas van en rejilla por filas (no admiten numeración alterna).
  if (geo.compact) {
    if (geo.rows > 1) return Math.floor(index / geo.cols) === 0 ? -1 : 1;
    return otherY < selfY ? -1 : 1;
  }
  // MISMA regla que el Diseñador de switches: con numeración alterna el puerto
  // de la fila de abajo es un índice par y antes salía por ARRIBA, así que el
  // cable rodeaba la tarjeta entera en vez de entrar por el borde de abajo.
  return exitSide(geo, index, selfY, otherY);
}

function groupKeyOf(node) {
  if (node.rack_id) return `rack:${node.rack_id}`;
  if (node.location_id) return `loc:${node.location_id}`;
  return 'misc';
}

function groupTitleOf(node) {
  if (node.rack_id) return `🗄 ${node.rack_name || 'Armario'}`;
  if (node.location_id) return `📍 ${node.location_name || 'Ubicación'}`;
  return '📦 Sin ubicación';
}

/** Coloca los nodos agrupados por armario / sede, en una rejilla de cajas. */
function layoutGroups(nodes) {
  const groups = new Map();
  nodes.forEach((node) => {
    const key = groupKeyOf(node);
    if (!groups.has(key)) {
      groups.set(key, { key, title: groupTitleOf(node), isRack: Boolean(node.rack_id), nodes: [] });
    }
    groups.get(key).nodes.push(node);
  });

  const ordered = [...groups.values()].sort((a, b) => {
    if (a.isRack !== b.isRack) return a.isRack ? -1 : 1;
    return a.title.localeCompare(b.title);
  });

  const positions = {};
  const boxes = {};
  let cursorX = 40;
  let cursorY = 40;
  let rowHeight = 0;
  let boxesInRow = 0;
  const maxBoxesPerRow = 3;

  ordered.forEach((group) => {
    const cards = group.nodes
      .slice()
      .sort(
        (a, b) => (KIND_META[a.kind]?.order ?? 99) - (KIND_META[b.kind]?.order ?? 99)
          || String(a.title).localeCompare(String(b.title)),
      );
    const perRow = group.isRack ? 2 : 4;
    let x = cursorX + GROUP_PAD;
    let y = cursorY + GROUP_HEADER + 16;
    let rowH = 0;
    let col = 0;
    let maxRight = x;
    cards.forEach((node) => {
      const geo = geoOfNode(node);
      if (col === 0) rowH = 0;
      positions[node.key] = { x: Math.round(x), y: Math.round(y) };
      rowH = Math.max(rowH, geo.height);
      maxRight = Math.max(maxRight, x + geo.width);
      col += 1;
      if (col >= perRow) {
        col = 0;
        x = cursorX + GROUP_PAD;
        y += rowH + 26;
      } else {
        x += geo.width + 26;
      }
    });
    const width = Math.max(240, maxRight - cursorX + GROUP_PAD);
    const height = Math.max(120, y + rowH - cursorY + GROUP_PAD);
    boxes[group.key] = {
      key: group.key,
      title: group.title,
      x: cursorX,
      y: cursorY,
      width,
      height,
    };

    rowHeight = Math.max(rowHeight, height);
    boxesInRow += 1;
    if (boxesInRow >= maxBoxesPerRow) {
      boxesInRow = 0;
      cursorX = 40;
      cursorY += rowHeight + GROUP_GAP_Y;
      rowHeight = 0;
    } else {
      cursorX += width + GROUP_GAP_X;
    }
  });

  return { positions, boxes };
}

/** Caja que envuelve los nodos de un grupo a partir de sus posiciones actuales. */
function boxFromNodes(groupNodes, positions) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  groupNodes.forEach((node) => {
    const pos = positions[node.key];
    if (!pos) return;
    const geo = geoOfNode(node);
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x + geo.width);
    maxY = Math.max(maxY, pos.y + geo.height);
  });
  if (!Number.isFinite(minX)) return null;
  return {
    x: minX - GROUP_PAD,
    y: minY - GROUP_HEADER - 12,
    width: maxX - minX + GROUP_PAD * 2,
    height: maxY - minY + GROUP_HEADER + 12 + GROUP_PAD,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────

function CablingDesigner({ companyId, embedded = false }) {
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [positions, setPositions] = useState({});
  const [hiddenKeys, setHiddenKeys] = useState([]);
  const [locations, setLocations] = useState([]);
  const [racks, setRacks] = useState([]);

  const [scopeMode, setScopeMode] = useState(() => {
    try { return localStorage.getItem('cmdb.cbd.scope') || 'company'; } catch { return 'company'; }
  });
  // Varias sedes a la vez: se suele cablear entre plantas/edificios.
  const [scopeLocationIds, setScopeLocationIds] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('cmdb.cbd.locations') || 'null');
      if (Array.isArray(raw)) return raw.map(Number).filter(Boolean);
      const legacy = localStorage.getItem('cmdb.cbd.location');   // formato antiguo
      return legacy ? [Number(legacy)].filter(Boolean) : [];
    } catch { return []; }
  });
  // Primera ubicación marcada: se usa como sede por defecto al crear algo.
  const scopeLocationId = scopeLocationIds.length ? String(scopeLocationIds[0]) : '';
  const [scopeRackIds, setScopeRackIds] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('cmdb.cbd.racks') || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch { return []; }
  });
  const [rackPickerOpen, setRackPickerOpen] = useState(false);
  const [rackSearch, setRackSearch] = useState('');

  const [kindFilter, setKindFilter] = useState(() => new Set(ALL_KINDS));
  const [search, setSearch] = useState('');
  // «Solo cableados» es un MODO que se recalcula con los filtros: un nodo entra
  // si tiene algún cable cuyo OTRO extremo también está dentro del filtro.
  const [onlyConnected, setOnlyConnected] = useState(() => {
    try { return localStorage.getItem('cmdb.cbd.onlyConnected') === '1'; } catch { return false; }
  });
  // Panel izquierdo (buscador + nodos): se puede plegar para dar ancho al lienzo.
  const [sideOpen, setSideOpen] = useState(() => {
    try { return localStorage.getItem('cmdb.cbd.side') !== 'closed'; } catch { return true; }
  });
  // Grupos por tipo de la lista, plegables.
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('cmdb.cbd.groups') || '[]');
      return new Set(Array.isArray(raw) ? raw : []);
    } catch { return new Set(); }
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [selected, setSelected] = useState(null); // { nodeKey, portKey }
  const [chainFocus, setChainFocus] = useState(null); // puerto del panel «Cadena»
  const [ghost, setGhost] = useState(null);
  const [hoverPortKey, setHoverPortKey] = useState(null);

  const [connectModal, setConnectModal] = useState(null); // { a, b }
  const [edgesModal, setEdgesModal] = useState(null);     // { a, edges }
  // Cable «en la mano» para cambiar un extremo de puerto sin desmontarlo.
  const [moveLink, setMoveLink] = useState(null);         // { edge, end, fromKey }
  // Disposición de puertos de un switch (misma que el Diseñador de switches).
  const [layoutModal, setLayoutModal] = useState(null);   // { node, rowsText, numbering }
  const [form, setForm] = useState({ cable_type: 'Cat6', cable_color: 'azul', length_m: '', notes: '' });
  const lastCableRef = useRef(readLastCable());
  const [newItem, setNewItem] = useState(null);           // { kind, ...form }
  const [elementForm, setElementForm] = useState(null);   // 'switch' | 'panel'

  const [chain, setChain] = useState(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isFull, setIsFull] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('cmdb.cbd.view') || 'canvas'; } catch { return 'canvas'; }
  });

  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const pageRef = useRef(null);
  const dragRef = useRef(null);
  const persistTimer = useRef(null);
  // Clic simple en un puerto ocupado: se espera a ver si es doble clic.
  const edgeClickTimer = useRef(null);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panRef = useRef(pan);
  panRef.current = pan;
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  const activePositionsRef = useRef({});
  const activeGeosRef = useRef(new Map());
  // Si el usuario hace zoom o mueve el lienzo, deja de reencuadrarse solo.
  const userAdjustedRef = useRef(false);
  const hiddenRef = useRef(hiddenKeys);
  hiddenRef.current = hiddenKeys;
  // Ámbito al que pertenecen los «ocultos» que tenemos en memoria. Sirve para
  // no volver a aplicar los del servidor en cada recarga (al cablear se recarga
  // el grafo y los ocultos antiguos reaparecían: había que desocultar de nuevo).
  const hiddenScopeRef = useRef(null);

  const scopeKey = useMemo(() => {
    if (scopeMode === 'racks' && scopeRackIds.length) {
      return `racks:${[...scopeRackIds].map(Number).sort((a, b) => a - b).join(',')}`;
    }
    if (scopeMode === 'location' && scopeLocationIds.length) {
      const ids = [...new Set(scopeLocationIds.map(Number))].sort((a, b) => a - b);
      return `loc:${ids.join(',')}`;
    }
    return 'company';
  }, [scopeMode, scopeRackIds, scopeLocationIds]);

  // ── Catálogos ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId).then((r) => setLocations(r.data || [])).catch(() => {});
    api.getRacks(null, companyId).then((r) => setRacks(r.data || [])).catch(() => {});
  }, [companyId]);

  // ── Carga del grafo ───────────────────────────────────────────────────────
  const params = useMemo(() => {
    const out = { scope_key: scopeKey };
    if (scopeMode === 'racks' && scopeRackIds.length) out.rack_ids = scopeRackIds.join(',');
    else if (scopeMode === 'location' && scopeLocationIds.length) out.location_ids = scopeLocationIds.join(',');
    return out;
  }, [scopeMode, scopeRackIds, scopeLocationIds, scopeKey]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCablingGraph(params, companyId);
      const data = res?.data || { nodes: [], edges: [] };
      const nodes = data.nodes || [];
      setGraph({ ...data, nodes, edges: data.edges || [] });

      const saved = {};
      const hidden = [];
      nodes.forEach((node) => {
        const row = data.layout?.[node.key];
        if (row && typeof row.x === 'number' && typeof row.y === 'number') {
          saved[node.key] = { x: row.x, y: row.y };
        }
        if (row?.hidden) hidden.push(node.key);
      });

      // Si la mayoría de nodos no tienen posición guardada, se organiza todo:
      // mezclar posiciones antiguas con nodos nuevos deja huecos raros.
      if (Object.keys(saved).length >= Math.ceil(nodes.length * 0.8) && nodes.length) {
        setPositions({ ...layoutGroups(nodes.filter((n) => !saved[n.key])).positions, ...saved });
      } else {
        setPositions(layoutGroups(nodes).positions);
      }
      // Los «ocultos» del servidor solo se aplican al entrar en un ámbito nuevo.
      // En las recargas automáticas (después de cablear, guardar un layout…)
      // manda lo que el usuario tiene delante.
      const scopeTag = `${companyId}:${scopeKey}`;
      if (hiddenScopeRef.current !== scopeTag) {
        hiddenScopeRef.current = scopeTag;
        hiddenRef.current = hidden;
        setHiddenKeys(hidden);
      }
      if (data.truncated) {
        setNotice(`Se muestran ${nodes.length} nodos: afina el ámbito o filtra por tipo.`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, params, scopeKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const onChange = () => setIsFull(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ── Nodos visibles y filtros ──────────────────────────────────────────────
  const hiddenSet = useMemo(() => new Set(hiddenKeys), [hiddenKeys]);

  /** ¿El nodo pasa los filtros de tipo y búsqueda? (sin mirar la visibilidad) */
  const matchesFilters = useCallback((node) => {
    if (!kindFilter.has(node.kind)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = `${node.title || ''} ${node.subtitle || ''} ${node.rack_name || ''} ${node.location_name || ''}`.toLowerCase();
    return hay.includes(q);
  }, [kindFilter, search]);

  const nodesInFilter = useMemo(
    () => (graph.nodes || []).filter(matchesFilters),
    [graph.nodes, matchesFilters],
  );

  // Nodo al que pertenece cada puerto (para saber los dos extremos de un cable).
  const nodeKeyByPort = useMemo(() => {
    const map = new Map();
    (graph.nodes || []).forEach((node) => {
      (node.ports || []).forEach((port) => map.set(port.key, node.key));
    });
    return map;
  }, [graph.nodes]);

  // «Solo cableados»: nodos con al menos un cable cuyos DOS extremos están
  // dentro del filtro actual. Si el patch panel está filtrado fuera, sus tomas
  // dejan de contar como cableadas (antes se colaba todo el cableado).
  const connectedInFilter = useMemo(() => {
    const inside = new Set(nodesInFilter.map((node) => node.key));
    const ok = new Set();
    (graph.edges || []).forEach((edge) => {
      const na = nodeKeyByPort.get(edge.a);
      const nb = nodeKeyByPort.get(edge.b);
      if (!na || !nb || na === nb) return;
      if (inside.has(na) && inside.has(nb)) {
        ok.add(na);
        ok.add(nb);
      }
    });
    return ok;
  }, [nodesInFilter, graph.edges, nodeKeyByPort]);

  const isHiddenByMode = useCallback(
    (node) => onlyConnected && !connectedInFilter.has(node.key),
    [onlyConnected, connectedInFilter],
  );

  const visibleNodes = useMemo(
    () => nodesInFilter.filter((node) => !hiddenSet.has(node.key) && !isHiddenByMode(node)),
    [nodesInFilter, hiddenSet, isHiddenByMode],
  );

  const geoByKey = useMemo(() => {
    const map = new Map();
    visibleNodes.forEach((node) => map.set(node.key, geoOfNode(node)));
    return map;
  }, [visibleNodes]);

  // ── Vista de armarios (rack frontal) ─────────────────────────────────────
  // Solo los nodos con posición U. Los equipos de escritorio (tomas, PCs)
  // siguen en el lienzo; aquí se avisa de cuántos quedan fuera.
  const isRackView = viewMode === 'racks';

  const rackNodes = useMemo(
    () => visibleNodes.filter((node) => node.rack_id && node.position_u),
    [visibleNodes],
  );

  const rackLayout = useMemo(
    () => buildRackLayout(rackNodes, graph.scope?.racks || []),
    [rackNodes, graph.scope],
  );

  const effectiveNodes = isRackView ? rackNodes : visibleNodes;
  const effectiveGeos = isRackView ? rackLayout.geos : geoByKey;
  const effectivePositions = isRackView ? rackLayout.positions : positions;
  activePositionsRef.current = effectivePositions;
  activeGeosRef.current = effectiveGeos;

  const portIndex = useMemo(() => {
    const map = new Map();
    effectiveNodes.forEach((node) => {
      const geo = effectiveGeos.get(node.key);
      (node.ports || []).forEach((port, index) => {
        map.set(port.key, { node, port, geo, index });
      });
    });
    return map;
  }, [effectiveNodes, effectiveGeos]);

  const visibleEdges = useMemo(
    () => (graph.edges || []).filter((edge) => portIndex.has(edge.a) && portIndex.has(edge.b)),
    [graph.edges, portIndex],
  );

  const edgeByKey = useMemo(() => {
    const map = new Map();
    (graph.edges || []).forEach((edge) => map.set(edge.key, edge));
    return map;
  }, [graph.edges]);

  // Rectángulos de las tarjetas visibles: el enrutado los usa para NO pasar por
  // encima de otros equipos (aquí hay muchas tarjetas juntas y si no el cable
  // las cruzaba). En la vista de armarios los cables van por el canal y no
  // hacen falta.
  const cardRects = useMemo(
    () => effectiveNodes
      .map((node) => {
        const pos = effectivePositions[node.key];
        const geo = effectiveGeos.get(node.key);
        if (!pos || !geo) return null;
        return {
          key: node.key,
          x1: pos.x,
          y1: pos.y,
          x2: pos.x + geo.width,
          y2: pos.y + geo.height,
        };
      })
      .filter(Boolean),
    [effectiveNodes, effectivePositions, effectiveGeos],
  );

  const groups = useMemo(() => {
    const map = new Map();
    visibleNodes.forEach((node) => {
      const key = groupKeyOf(node);
      if (!map.has(key)) map.set(key, { key, title: groupTitleOf(node), nodes: [] });
      map.get(key).nodes.push(node);
    });
    return [...map.values()];
  }, [visibleNodes]);

  // Lista lateral: incluye los ocultos (con el ojo cerrado) para poder
  // recuperarlos, y también los que «Solo cableados» deja fuera del lienzo.
  const listNodes = useMemo(() => {
    const base = isRackView
      ? (graph.nodes || []).filter((node) => node.rack_id && node.position_u)
      : (graph.nodes || []);
    return base.filter(matchesFilters);
  }, [graph.nodes, isRackView, matchesFilters]);

  // Grupos de la lista por tipo, en el orden de KIND_META (plegables).
  const listGroups = useMemo(() => {
    const map = new Map();
    listNodes.forEach((node) => {
      if (!map.has(node.kind)) map.set(node.kind, []);
      map.get(node.kind).push(node);
    });
    return [...map.entries()].sort(
      (a, b) => (KIND_META[a[0]]?.order ?? 99) - (KIND_META[b[0]]?.order ?? 99),
    );
  }, [listNodes]);

  const toggleGroup = (kind) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind); else next.add(kind);
      try { localStorage.setItem('cmdb.cbd.groups', JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  };

  const toggleSide = () => {
    setSideOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem('cmdb.cbd.side', next ? 'open' : 'closed'); } catch { /* noop */ }
      return next;
    });
  };

  const toggleOnlyConnected = () => {
    setOnlyConnected((prev) => {
      const next = !prev;
      try { localStorage.setItem('cmdb.cbd.onlyConnected', next ? '1' : '0'); } catch { /* noop */ }
      if (next) {
        const excluded = nodesInFilter.length - connectedInFilter.size;
        setNotice(
          excluded > 0
            ? `Solo cableados: ${connectedInFilter.size} dentro del filtro (${excluded} sin cable completo quedan fuera)`
            : 'Solo cableados: todo lo filtrado tiene cable a otro extremo visible',
        );
      } else {
        setNotice(null);
      }
      return next;
    });
  };

  const showAllNodes = () => {
    hiddenRef.current = [];
    setHiddenKeys([]);
    persistNow();
  };

  /** Oculta de golpe TODO lo que hay en la lista (con los filtros actuales).
   *  Después se van mostrando de uno en uno los que interesen. */
  const hideAllListed = () => {
    const hiddenNow = new Set(hiddenRef.current);
    const pending = listNodes.filter((node) => !hiddenNow.has(node.key)).map((node) => node.key);
    if (!pending.length) return;
    const next = [...hiddenNow, ...pending];
    hiddenRef.current = next;
    setHiddenKeys(next);
    persistNow();
    setNotice(`${pending.length} elementos ocultos: muestra en la lista los que necesites`);
  };

  /** Oculta o muestra de golpe todos los nodos de un grupo (por tipo). */
  const setGroupHidden = (rows, shouldHide) => {
    if (!rows.length) return;
    const keys = new Set(rows.map((row) => row.key));
    const next = shouldHide
      ? [...new Set([...hiddenRef.current, ...keys])]
      : hiddenRef.current.filter((key) => !keys.has(key));
    hiddenRef.current = next;
    setHiddenKeys(next);
    persistNow();
    const label = KIND_META[rows[0]?.kind]?.label || '';
    setNotice(`${rows.length} ${shouldHide ? 'ocultos' : 'visibles'} · ${label}`);
  };

  const selectedPort = useMemo(() => {
    if (!chainFocus) return null;
    const hit = portIndex.get(chainFocus.portKey);
    return hit ? { ...hit, ...chainFocus } : null;
  }, [chainFocus, portIndex]);

  // ── Encuadre / pan / zoom ─────────────────────────────────────────────────
  const fitView = useCallback(() => {
    const box = canvasRef.current?.getBoundingClientRect?.();
    if (!box || box.width < 40) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const nodes = isRackView ? rackNodes : visibleNodes;
    nodes.forEach((node) => {
      const pos = isRackView ? rackLayout.positions[node.key] : positionsRef.current[node.key];
      if (!pos) return;
      const geo = isRackView ? rackLayout.geos.get(node.key) : geoOfNode(node);
      if (!geo) return;
      minX = Math.min(minX, pos.x);
      minY = Math.min(minY, pos.y);
      maxX = Math.max(maxX, pos.x + geo.width);
      maxY = Math.max(maxY, pos.y + geo.height);
    });
    if (isRackView) {
      rackLayout.boxes.forEach((rackBox) => {
        minX = Math.min(minX, rackBox.x);
        minY = Math.min(minY, rackBox.y);
        maxX = Math.max(maxX, rackBox.x + rackBox.width);
        maxY = Math.max(maxY, rackBox.y + rackBox.height);
      });
    }
    if (!Number.isFinite(minX)) return;
    const pad = 46;
    const bw = Math.max(1, maxX - minX);
    const bh = Math.max(1, maxY - minY);
    const z = clamp(Math.min((box.width - pad * 2) / bw, (box.height - pad * 2) / bh), 0.25, 1.25);
    setZoom(z);
    setPan({
      x: (box.width - bw * z) / 2 - minX * z,
      y: (box.height - bh * z) / 2 - minY * z,
    });
  }, [isRackView, rackNodes, rackLayout, visibleNodes]);

  const fitViewRef = useRef(fitView);
  fitViewRef.current = fitView;

  // Al cambiar de vista el encuadre anterior ya no sirve: reencuadra.
  useEffect(() => {
    setSelected(null);
    setChainFocus(null);
    setGhost(null);
    userAdjustedRef.current = false;
    const timer = setTimeout(() => fitViewRef.current?.(), 120);
    return () => clearTimeout(timer);
  }, [viewMode]);

  // El grafo puede llegar después de montar (o cambiar de ámbito): encuadra
  // en cuanto haya nodos por primera vez, sin pisar el zoom del usuario luego.
  const fittedCountRef = useRef(-1);
  useEffect(() => {
    const count = effectiveNodes.length;
    if (!count) {
      fittedCountRef.current = -1;
      return undefined;
    }
    const firstLoad = fittedCountRef.current <= 0;
    fittedCountRef.current = count;
    if (!firstLoad) return undefined;
    const timer = setTimeout(() => fitViewRef.current?.(), 90);
    return () => clearTimeout(timer);
  }, [effectiveNodes.length]);

  // El contenedor cambia de tamaño (pestañas, panel lateral, ventana): si el
  // usuario no ha tocado el zoom, se vuelve a encuadrar para no cortar el rack.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let timer = null;
    const ro = new ResizeObserver(() => {
      if (userAdjustedRef.current) return;
      clearTimeout(timer);
      timer = setTimeout(() => fitViewRef.current?.(), 140);
    });
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      userAdjustedRef.current = true;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const current = zoomRef.current || 1;
      const next = clamp(current * (e.deltaY < 0 ? 1.12 : 1 / 1.12), ZMIN, ZMAX);
      const ratio = next / current;
      const p = panRef.current;
      setPan({ x: mx - (mx - p.x) * ratio, y: my - (my - p.y) * ratio });
      setZoom(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const worldFromEvent = (e) => {
    const rect = svgRef.current?.getBoundingClientRect?.();
    if (!rect) return null;
    return {
      x: (e.clientX - rect.left - panRef.current.x) / (zoomRef.current || 1),
      y: (e.clientY - rect.top - panRef.current.y) / (zoomRef.current || 1),
    };
  };

  const onCanvasDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest && (e.target.closest('.cbd-card') || e.target.closest('.cbd-port') || e.target.closest('.cbd-group'))) return;
    const p = panRef.current;
    dragRef.current = { pan: true, mx: e.clientX, my: e.clientY, px: p.x, py: p.y };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || !d.pan) return;
      if (Math.abs(ev.clientX - d.mx) + Math.abs(ev.clientY - d.my) > 3) userAdjustedRef.current = true;
      setPan({ x: d.px + (ev.clientX - d.mx), y: d.py + (ev.clientY - d.my) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Persistencia del layout (por empresa) ────────────────────────────────
  // Se manda `hidden` SIEMPRE (true o false): antes solo se enviaban los
  // ocultos y un «mostrar» nunca llegaba al servidor, así que al recargar el
  // nodo volvía a estar oculto.
  const persistLayout = useCallback(() => {
    const hiddenNow = new Set(hiddenRef.current);
    const payload = {};
    Object.entries(positionsRef.current).forEach(([key, pos]) => {
      payload[key] = { x: pos.x, y: pos.y, hidden: hiddenNow.has(key) };
    });
    hiddenNow.forEach((key) => {
      if (!payload[key]) payload[key] = { hidden: true };
    });
    return api.saveCablingLayout(scopeKey, payload, companyId).catch((e) => setError(e.message));
  }, [companyId, scopeKey]);

  const schedulePersist = useCallback(() => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      persistTimer.current = null;
      persistLayout();
    }, 700);
  }, [persistLayout]);

  /** Guarda ya (sin esperar el retardo): ocultar/mostrar debe ser inmediato. */
  const persistNow = useCallback(() => {
    if (persistTimer.current) {
      clearTimeout(persistTimer.current);
      persistTimer.current = null;
    }
    persistLayout();
  }, [persistLayout]);

  // ── Arrastrar tarjeta o caja de armario ─────────────────────────────────
  const startDrag = (e, node) => {
    if (isRackView) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const start = positionsRef.current[node.key] || { x: 0, y: 0 };
    dragRef.current = { keys: [node.key], origins: { [node.key]: { ...start } }, mx: e.clientX, my: e.clientY, moved: false };
    attachDragHandlers();
  };

  const startGroupDrag = (e, group) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const origins = {};
    group.nodes.forEach((node) => {
      const pos = positionsRef.current[node.key];
      if (pos) origins[node.key] = { ...pos };
    });
    dragRef.current = {
      keys: Object.keys(origins), origins, mx: e.clientX, my: e.clientY, moved: false,
    };
    attachDragHandlers();
  };

  const attachDragHandlers = () => {
    const z0 = zoomRef.current || 1;
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || d.pan) return;
      const dx = (ev.clientX - d.mx) / z0;
      const dy = (ev.clientY - d.my) / z0;
      if (!d.moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) d.moved = true;
      if (!d.moved) return;
      const next = { ...positionsRef.current };
      d.keys.forEach((key) => {
        const origin = d.origins[key];
        if (!origin) return;
        next[key] = { x: Math.round(origin.x + dx), y: Math.round(origin.y + dy) };
      });
      positionsRef.current = next;
      setPositions(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const moved = dragRef.current?.moved;
      dragRef.current = null;
      if (moved) schedulePersist();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const autoOrganize = () => {
    const { positions: next } = layoutGroups(visibleNodes);
    positionsRef.current = next;
    setPositions(next);
    schedulePersist();
    setNotice('Lienzo reorganizado por armario y sede');
    setTimeout(fitView, 80);
  };

  // Esc cancela el «cable en la mano».
  useEffect(() => {
    if (!moveLink) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setMoveLink(null);
        setNotice(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moveLink]);

  const hideNode = (key) => {
    const next = hiddenRef.current.includes(key)
      ? hiddenRef.current.filter((x) => x !== key)
      : [...hiddenRef.current, key];
    hiddenRef.current = next;
    setHiddenKeys(next);
    persistNow();
  };

  const focusNode = (node) => {
    if (hiddenRef.current.includes(node.key)) hideNode(node.key);
    const pos = activePositionsRef.current[node.key] || positionsRef.current[node.key];
    const box = canvasRef.current?.getBoundingClientRect?.();
    if (!pos || !box) return;
    const geo = activeGeosRef.current.get(node.key) || geoOfNode(node);
    const z = Math.max(zoomRef.current || 1, 0.8);
    userAdjustedRef.current = true;
    setZoom(z);
    setPan({ x: box.width / 2 - (pos.x + geo.width / 2) * z, y: box.height / 2 - (pos.y + geo.height / 2) * z });
  };

  // ── Cadena del puerto seleccionado ───────────────────────────────────────
  // Se depende de tipo/id (primitivos): `selectedPort` es un objeto nuevo en
  // cada render y con él como dependencia se dispararía un fetch en bucle.
  const chainPortType = selectedPort?.port?.type;
  const chainPortId = selectedPort?.port?.id;

  useEffect(() => {
    let cancelled = false;
    if (!chainPortType || !chainPortId || !companyId) { setChain(null); return undefined; }
    setChainLoading(true);
    api.getCablingChain(chainPortType, chainPortId, companyId)
      .then((res) => { if (!cancelled) setChain(res?.data?.links || []); })
      .catch(() => { if (!cancelled) setChain([]); })
      .finally(() => { if (!cancelled) setChainLoading(false); });
    return () => { cancelled = true; };
  }, [chainPortType, chainPortId, companyId]);

  // ── Cablear: clic en puerto ──────────────────────────────────────────────
  const portTip = (port, node) => {
    const cables = (port.edges || []).length;
    const bits = [
      `${KIND_META[node.kind]?.label || node.kind}: ${node.title}`,
      `${port.label}${port.number != null ? ` (${port.number})` : ''}`,
    ];
    if (port.external) bits.push('Cable fuera del ámbito actual');
    else if (cables) bits.push(`${cables} cable${cables > 1 ? 's' : ''}`);
    else bits.push('Libre');
    if (port.meta?.vlan) bits.push(`VLAN ${port.meta.vlan}`);
    if (port.meta?.poe) bits.push('PoE');
    if (port.type === 'wall_outlet') {
      bits.push('panel por detrás + equipo por delante');
    }
    return bits.join(' · ');
  };

  const openEdgesModal = (node, port) => {
    const edges = (port.edges || []).map((key) => edgeByKey.get(key)).filter(Boolean);
    setEdgesModal({ node, port, edges });
    setConnectModal(null);
    setSelected(null);
    setGhost(null);
    setChainFocus({ nodeKey: node.key, portKey: port.key });
  };

  /**
   * Doble clic en un puerto cableado: enseña TODO lo que cuelga de él.
   * `tracePath` recorre el camino puerto a puerto (latiguillo → panel → pared →
   * toma → equipo…); de ahí se sacan los nodos y se desocultan.
   */
  const revealPath = (node, port) => {
    if (edgeClickTimer.current) {
      clearTimeout(edgeClickTimer.current);
      edgeClickTimer.current = null;
    }
    const path = tracePath(graph.edges || [], port.key);
    const nodeKeys = new Set([node.key]);
    path.ports.forEach((portKey) => {
      const nodeKey = nodeKeyByPort.get(portKey);
      if (nodeKey) nodeKeys.add(nodeKey);
    });
    const nextHidden = hiddenRef.current.filter((key) => !nodeKeys.has(key));
    const shownNow = nodeKeys.size - (hiddenRef.current.length - nextHidden.length);
    hiddenRef.current = nextHidden;
    setHiddenKeys(nextHidden);
    persistNow();
    // Con «Solo cableados» activo, el otro extremo podría estar fuera del
    // filtro y no verse: se desactiva para enseñar el camino entero.
    const wasFiltered = onlyConnected;
    if (wasFiltered) {
      setOnlyConnected(false);
      try { localStorage.setItem('cmdb.cbd.onlyConnected', '0'); } catch { /* noop */ }
    }
    setEdgesModal(null);
    setChainFocus({ nodeKey: node.key, portKey: port.key });
    setNotice(
      `${nodeKeys.size} nodos en el camino de ${node.title} · ${port.label}`
      + `${shownNow > 0 ? ` (${shownNow} estaban ocultos)` : ''}`
      + `${wasFiltered ? ' · «Solo cableados» desactivado' : ''}`,
    );
    setTimeout(() => fitViewRef.current?.(), 120);
  };

  // Bocas que aguantan más de un cable: la del panel (latiguillo + pared +
  // enlace entre armarios) y la toma de pared (pared por detrás + equipo).
  const MAX_CABLES = { panel_port: 3, wall_outlet: 2 };
  const canAddCable = (port, count) => {
    const max = MAX_CABLES[port?.type];
    return Boolean(max) && count < max;
  };

  /** Cierra el diálogo y deja el puerto «armado» para elegir el otro extremo. */
  const startCableFrom = ({ node, port }) => {
    setEdgesModal(null);
    setSelected({ nodeKey: node.key, portKey: port.key });
    setChainFocus({ nodeKey: node.key, portKey: port.key });
    setNotice(`Elige el otro extremo del cable de ${node.title} · ${port.label}`);
  };

  /** Cierra el diálogo y deja el cable «en la mano» para soltarlo en otra boca. */
  const startMoveLink = (node, port, edge) => {
    setEdgesModal(null);
    setSelected(null);
    setGhost(null);
    setChainFocus(null);
    setMoveLink({
      edge,
      end: edge.a === port.key ? 'a' : 'b',
      fromKey: port.key,
    });
    setNotice(
      `Moviendo el extremo de ${node.title} · ${port.label}: haz clic en el puerto nuevo (Esc para cancelar).`,
    );
  };

  /** Doble clic en un switch: filas y numeración, igual que en el Diseñador de
   *  switches. Se guarda en el MISMO sitio (`RackElement.configuration`), así
   *  que los dos lienzos enseñan siempre lo mismo. */
  const openLayoutModal = (node) => {
    if (node.kind !== 'switch') return;
    setLayoutModal({
      node,
      rowsText: Array.isArray(node.meta?.canvas_rows) ? node.meta.canvas_rows.join(', ') : '',
      numbering: node.meta?.canvas_numbering === 'alternate' ? 'alternate' : 'rows',
    });
  };

  const saveLayoutModal = async () => {
    if (!layoutModal || saving) return;
    const rows = layoutModal.rowsText
      .split(',')
      .map((part) => parseInt(part.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    setSaving(true);
    setError(null);
    try {
      await api.saveSwitchCanvasLayout(
        {
          [layoutModal.node.id]: {
            rows: rows.length ? rows : null,
            numbering: layoutModal.numbering,
          },
        },
        undefined,
        companyId,
      );
      const title = layoutModal.node.title;
      setLayoutModal(null);
      setNotice(`Disposición de ${title} actualizada (también en el Diseñador de switches)`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancelMoveLink = () => {
    setMoveLink(null);
    setNotice(null);
  };

  const commitMoveLink = async (port) => {
    if (!moveLink || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.moveCablingLink(
        {
          kind: moveLink.edge.kind,
          id: moveLink.edge.linked_id,
          end: moveLink.end,
          to: { type: port.type, id: port.id },
        },
        companyId,
      );
      setNotice(`Cable movido a ${port.label}`);
      setMoveLink(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePortClick = (e, node, port) => {
    e.stopPropagation();
    setError(null);
    setNotice(null);

    // Modo «mover cable»: el siguiente clic es el puerto de destino.
    if (moveLink) {
      if (port.key === moveLink.fromKey) {
        setNotice('Elige un puerto distinto del actual');
        return;
      }
      commitMoveLink(port);
      return;
    }

    if (selected) {
      if (selected.portKey === port.key) {
        setSelected(null);
        setGhost(null);
        setChainFocus(null);
        return;
      }
      if (port.external) {
        setNotice('Ese puerto está ocupado por un cable que se sale del ámbito mostrado');
        return;
      }
      const hit = portIndex.get(selected.portKey);
      if (!hit) return;
      const a = { node: hit.node, port: hit.port };
      const b = { node, port };
      const kind = linkKindOf(a, b);
      const sameRack = Boolean(a.node.rack_id && a.node.rack_id === b.node.rack_id);
      // Se recuerda el último cable: lo normal es repetir tipo, color y medida.
      const last = lastCableRef.current || {};
      setForm({
        cable_type: last.cable_type
          || (kind === 'backbone' && !sameRack ? 'Fibra SM (OS2)' : 'Cat6'),
        cable_color: last.cable_color || (kind === 'backbone' ? 'violeta' : 'azul'),
        length_m: last.length_m === undefined || last.length_m === null ? '' : String(last.length_m),
        notes: '',
      });
      setConnectModal({ a, b, kind, sameRack });
      setSelected(null);
      setGhost(null);
      setChainFocus(null);
      return;
    }

    if ((port.edges || []).length) {
      // Se retrasa un pelín: si el usuario hace DOBLE clic, ese gesto revela
      // todo el camino y no tiene sentido abrir y cerrar el diálogo.
      if (edgeClickTimer.current) clearTimeout(edgeClickTimer.current);
      edgeClickTimer.current = setTimeout(() => {
        edgeClickTimer.current = null;
        openEdgesModal(node, port);
      }, 220);
      return;
    }
    if (port.external) {
      setNotice('Ese puerto está ocupado por un cable que se sale del ámbito mostrado');
      return;
    }
    setSelected({ nodeKey: node.key, portKey: port.key });
    setChainFocus({ nodeKey: node.key, portKey: port.key });
  };

  const linkKindOf = (a, b) => {
    const types = new Set([a.port.type, b.port.type]);
    if (types.has('wall_panel') || (types.has('wall_outlet') && types.has('panel_port'))) return 'wall_run';
    if (types.size === 1 && types.has('panel_port')) return 'backbone';
    return 'port_connection';
  };

  const doConnect = async () => {
    if (!connectModal || saving) return;
    const { a, b, kind } = connectModal;
    setSaving(true);
    setError(null);
    try {
      const cable = kind === 'wall_run'
        ? { notes: form.notes || null }
        : {
          cable_type: form.cable_type || null,
          cable_color: form.cable_color || null,
          length_m: form.length_m === '' ? null : Number(form.length_m),
          notes: form.notes || null,
        };
      const res = await api.createCablingLink(
        { type: a.port.type, id: a.port.id },
        { type: b.port.type, id: b.port.id },
        cable,
        companyId,
      );
      const edge = res?.data || {};
      const auto = edge.auto_cable ? ' · fibra por defecto entre armarios' : '';
      // El siguiente cable sale con estos mismos valores.
      if (kind !== 'wall_run') {
        const remembered = {
          cable_type: form.cable_type || null,
          cable_color: form.cable_color || null,
          length_m: form.length_m,
        };
        lastCableRef.current = remembered;
        saveLastCable(remembered);
      }
      setConnectModal(null);
      setNotice(`Cable creado: ${a.node.title} ${a.port.label} ↔ ${b.node.title} ${b.port.label}${auto}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdgeForm = (edge) => {
    setForm({
      cable_type: edge.cable_type || 'Cat6',
      cable_color: edge.cable_color || 'azul',
      length_m: edge.length_m ?? '',
      notes: edge.notes || '',
    });
    setEdgesModal((current) => (current ? { ...current, editing: edge } : current));
  };

  const doUpdateEdge = async (edge) => {
    setSaving(true);
    setError(null);
    try {
      await api.updateCablingLink(edge.kind, edge.linked_id, {
        cable_type: form.cable_type || null,
        cable_color: form.cable_color || null,
        length_m: form.length_m === '' ? null : Number(form.length_m),
        notes: form.notes || null,
      }, companyId);
      setEdgesModal(null);
      setNotice('Cable actualizado');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const doDeleteEdge = async (edge) => {
    setSaving(true);
    setError(null);
    try {
      await api.deleteCablingLink(edge.kind, edge.linked_id, companyId);
      setEdgesModal(null);
      setNotice('Cable desconectado');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Aristas: geometría ───────────────────────────────────────────────────
  const laneByEdge = useMemo(() => {
    const items = [];
    visibleEdges.forEach((edge) => {
      const a = portIndex.get(edge.a);
      const b = portIndex.get(edge.b);
      const pa = effectivePositions[a?.node.key];
      const pb = effectivePositions[b?.node.key];
      if (!a || !b || !pa || !pb) return;
      const ax = pa.x + offsetOf(a.geo, a.index).x;
      const bx = pb.x + offsetOf(b.geo, b.index).x;
      items.push({ key: edge.key, x1: Math.min(ax, bx), x2: Math.max(ax, bx) });
    });
    items.sort((a, b) => a.x1 - b.x1 || a.x2 - b.x2);
    const laneEnd = [];
    const map = new Map();
    items.forEach((item) => {
      let lane = laneEnd.findIndex((end) => end <= item.x1);
      if (lane === -1) {
        lane = laneEnd.length;
        laneEnd.push(item.x2);
      } else {
        laneEnd[lane] = item.x2;
      }
      map.set(item.key, lane);
    });
    return map;
  }, [visibleEdges, portIndex, effectivePositions]);

  const edgeGeometry = useCallback((edge) => {
    const a = portIndex.get(edge.a);
    const b = portIndex.get(edge.b);
    if (!a || !b) return null;
    const pa = effectivePositions[a.node.key];
    const pb = effectivePositions[b.node.key];
    if (!pa || !pb) return null;
    const oa = offsetOf(a.geo, a.index);
    const ob = offsetOf(b.geo, b.index);
    const ax = pa.x + oa.x;
    const ay = pa.y + oa.y;
    const bx = pb.x + ob.x;
    const by = pb.y + ob.y;
    const sideA = sideOf(a.geo, a.index, ay, by);
    const sideB = sideOf(b.geo, b.index, by, ay);
    const ya = sideA === -1 ? pa.y : pa.y + a.geo.height;
    const yb = sideB === -1 ? pb.y : pb.y + b.geo.height;
    const rectA = { x1: pa.x, y1: pa.y, x2: pa.x + a.geo.width, y2: pa.y + a.geo.height };
    const rectB = { x1: pb.x, y1: pb.y, x2: pb.x + b.geo.width, y2: pb.y + b.geo.height };
    const lane = laneByEdge.get(edge.key) || 0;
    // En la vista de armarios el cable sale al canal lateral del rack y cruza
    // por el pasillo; en el lienzo, por el carril ortogonal de siempre.
    let points = null;
    if (a.geo.mode === 'rack') {
      points = rackCablePoints(
        ax, ay, bx, by,
        rackLayout.boxByNode.get(a.node.key),
        rackLayout.boxByNode.get(b.node.key),
      );
    }
    if (!points) {
      const obstacles = cardRects.filter(
        (rect) => rect.key !== a.node.key && rect.key !== b.node.key,
      );
      points = cableRoute(ax, ya, bx, yb, sideA, sideB, lane, rectA, rectB, obstacles);
    }
    return {
      d: roundedPath(points),
      mid: polylineMid(points),
      ax, ay, bx, by, sideA, sideB,
    };
  }, [portIndex, effectivePositions, laneByEdge, rackLayout, cardRects]);

  // Un puerto puede llevar VARIOS cables (toma, boca de panel) y cada uno sale
  // por su lado: se guardan todos o el cable que va al otro lado se queda
  // colgado en el borde de la tarjeta sin llegar al conector.
  const leads = useMemo(() => {
    const map = new Map();
    const push = (key, lead) => {
      const list = map.get(key) || [];
      if (!list.some((l) => l.side === lead.side)) list.push(lead);
      map.set(key, list);
    };
    visibleEdges.forEach((edge) => {
      const geo = edgeGeometry(edge);
      if (!geo) return;
      push(edge.a, { x: geo.ax, y: geo.ay, side: geo.sideA, color: edge.cable_color, kind: edge.kind });
      push(edge.b, { x: geo.bx, y: geo.by, side: geo.sideB, color: edge.cable_color, kind: edge.kind });
    });
    return map;
  }, [visibleEdges, edgeGeometry]);

  const ghostLine = useMemo(() => {
    if (!selected || !ghost) return null;
    const hit = portIndex.get(selected.portKey);
    if (!hit) return null;
    const pos = activePositionsRef.current[hit.node.key];
    if (!pos) return null;
    const off = offsetOf(hit.geo, hit.index);
    const x1 = pos.x + off.x;
    const y1 = pos.y + off.y;
    const side = sideOf(hit.geo, hit.index, y1, ghost.y);
    const yEdge = side === -1 ? pos.y : pos.y + hit.geo.height;
    const reach = clamp(Math.hypot(ghost.x - x1, ghost.y - y1) * 0.3, 26, 130);
    return {
      d: `M ${x1} ${yEdge} C ${x1} ${yEdge + side * reach}, ${ghost.x} ${ghost.y - side * reach * 0.35}, ${ghost.x} ${ghost.y}`,
    };
  }, [selected, ghost, portIndex]);

  // ── Estilos ──────────────────────────────────────────────────────────────
  const portState = (port) => {
    if (port.active === false) return 'off';
    if (port.external) return 'external';
    if ((port.edges || []).length) return 'busy';
    if (port.meta?.legacy) return 'legacy';
    return 'free';
  };

  const toggleKind = (kind) => {
    setKindFilter((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const changeScope = (mode) => {
    setScopeMode(mode);
    try { localStorage.setItem('cmdb.cbd.scope', mode); } catch { /* noop */ }
  };

  /** Selección múltiple de ubicaciones (se conserva entre sesiones). */
  const setScopeLocations = (ids) => {
    const next = [...new Set((ids || []).map(Number).filter(Boolean))];
    setScopeLocationIds(next);
    try { localStorage.setItem('cmdb.cbd.locations', JSON.stringify(next)); } catch { /* noop */ }
  };

  const toggleRack = (id) => {
    setScopeRackIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem('cmdb.cbd.racks', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
      return;
    }
    pageRef.current?.requestFullscreen?.().catch((e) => setError(e.message));
  };

  const toggleView = () => {
    setViewMode((mode) => {
      const next = mode === 'canvas' ? 'racks' : 'canvas';
      try { localStorage.setItem('cmdb.cbd.view', next); } catch { /* noop */ }
      return next;
    });
  };

  const createNewItem = async () => {
    if (!newItem || saving) return;
    const { kind, code, name, location_id, room } = newItem;
    if (!code && !name) { setError('Pon un nombre o un código'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: code || name,
        name: name || code,
        location_id: location_id ? Number(location_id) : null,
        room: room || null,
        status: 'active',
      };
      if (kind === 'outlet') {
        await api.createWallOutlet({ ...payload, outlet_type: 'rj45' }, companyId);
      } else if (kind === 'workstation') {
        await api.createWorkstation({ ...payload, device_type: 'pc' }, companyId);
      } else if (kind === 'printer') {
        await api.createPrinter(payload, companyId);
      } else if (kind === 'ip_phone') {
        await api.createIPPhone(payload, companyId);
      }
      setNewItem(null);
      // En ámbito de armarios, un equipo nuevo de la sede no se vería: se
      // cambia a la sede del elemento para que aparezca en el lienzo.
      if (scopeMode === 'racks' && location_id) {
        setScopeMode('location');
        setScopeLocations([Number(location_id)]);
      }
      setNotice(`${KIND_META[kind].label} creada: ya está en el lienzo`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const racksOfScope = useMemo(() => {
    const q = rackSearch.trim().toLowerCase();
    return racks.filter((rack) => {
      if (scopeLocationIds.length && !matchesLocation(locations, scopeLocationIds, rack.location_id)) return false;
      if (!q) return true;
      return `${rack.name || ''} ${rack.code || ''}`.toLowerCase().includes(q);
    });
  }, [racks, rackSearch, scopeLocationIds, locations]);

  const stats = useMemo(() => {
    const counts = {};
    (graph.nodes || []).forEach((node) => { counts[node.kind] = (counts[node.kind] || 0) + 1; });
    return counts;
  }, [graph.nodes]);

  // Cables que tocan directamente el puerto bajo el ratón (se pintan más
  // gruesos; el camino completo lo lleva `activePath`).
  const highlightEdges = useMemo(() => {
    const set = new Set();
    if (!hoverPortKey) return set;
    (graph.edges || []).forEach((edge) => {
      if (edge.a === hoverPortKey || edge.b === hoverPortKey) set.add(edge.key);
    });
    return set;
  }, [hoverPortKey, graph.edges]);

  // Camino completo del puerto seleccionado: cables y puertos que hay que
  // iluminar. Los eslabones de `follow_chain` traen `kind` + `linked_id`, que
  // es justo la clave de la arista en el grafo (`pc:`, `backbone:`, `wall:`).
  const chainPath = useMemo(() => {
    const edges = new Set();
    const ports = new Set();
    (chain || []).forEach((link) => {
      if (link.from?.key) ports.add(link.from.key);
      if (link.to?.key) ports.add(link.to.key);
      if (link.kind === 'port_connection') edges.add(`pc:${link.linked_id}`);
      else if (link.kind === 'backbone') edges.add(`backbone:${link.linked_id}`);
      else if (link.kind === 'wall_run') edges.add(`wall:${link.linked_id}`);
      // `outlet_through` no es un cable: solo añade las dos caras de la toma.
    });
    return { edges, ports };
  }, [chain]);

  const hoverPath = useMemo(
    () => tracePath(graph.edges || [], hoverPortKey),
    [graph.edges, hoverPortKey],
  );

  // Lo que se pinta encendido: el camino del puerto bajo el ratón y, si no hay
  // ratón, el del puerto seleccionado (que viene del backend con sus eslabones).
  const activePath = hoverPath.edges.size ? hoverPath : chainPath;

  const hasPath = activePath.edges.size > 0 || activePath.ports.size > 0;

  const pathNodes = useMemo(() => {
    const set = new Set();
    visibleNodes.forEach((node) => {
      if ((node.ports || []).some((port) => activePath.ports.has(port.key))) set.add(node.key);
    });
    return set;
  }, [visibleNodes, activePath]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div ref={pageRef} className={`swd-page cbd-page${embedded ? ' is-tabbed' : ''}${isFull ? ' is-fullscreen' : ''}`}>
      <div className="swd-toolbar">
        <span className="swd-title">
          <LinkIcon className="cbd-ico is-title" aria-hidden="true" />
          Diseñador de Cableado
        </span>
        <span className="swd-stats">
          {visibleNodes.length}/{graph.nodes?.length || 0} nodos · {visibleEdges.length} cables
        </span>
        <span className="swd-spacer" />

        <button
          type="button"
          className="swd-btn"
          onClick={toggleSide}
          aria-pressed={!sideOpen}
          title={sideOpen ? 'Ocultar el panel de elementos' : 'Mostrar el panel de elementos'}
        >
          {sideOpen
            ? <ChevronDoubleLeftIcon className="cbd-ico" aria-hidden="true" />
            : <ChevronDoubleRightIcon className="cbd-ico" aria-hidden="true" />}
        </button>

        <label className="swd-scenario" title="Ámbito del lienzo">
          Ámbito
          <select value={scopeMode} onChange={(e) => changeScope(e.target.value)}>
            {Object.entries(SCOPES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>

        {scopeMode === 'location' && (
          <LocationFilter
            companyId={companyId}
            value={scopeLocationIds}
            onChange={setScopeLocations}
            label="Ubicaciones"
          />
        )}

        {scopeMode === 'racks' && (
          <div className="cbd-rack-picker">
            <button type="button" className="swd-btn" onClick={() => setRackPickerOpen((v) => !v)}>
              🗄 {scopeRackIds.length ? `${scopeRackIds.length} armarios` : 'Elegir armarios'}
            </button>
            {rackPickerOpen && (
              <div className="cbd-rack-pop">
                <input
                  type="search"
                  value={rackSearch}
                  onChange={(e) => setRackSearch(e.target.value)}
                  placeholder="Buscar armario…"
                />
                <div className="cbd-rack-list">
                  {racksOfScope.map((rack) => (
                    <label key={rack.id} className="cbd-rack-row">
                      <input
                        type="checkbox"
                        checked={scopeRackIds.includes(rack.id)}
                        onChange={() => toggleRack(rack.id)}
                      />
                      <span>{rack.name}</span>
                      <em>{locations.find((l) => String(l.id) === String(rack.location_id))?.name || ''}</em>
                    </label>
                  ))}
                  {!racksOfScope.length && <div className="cbd-rack-empty">Sin armarios que coincidan</div>}
                </div>
                <button type="button" className="swd-btn is-primary" onClick={() => setRackPickerOpen(false)}>
                  Aplicar
                </button>
              </div>
            )}
          </div>
        )}

        <select
          className="swd-btn cbd-new"
          value=""
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'switch' || value === 'panel') setElementForm(value);
            else if (value) {
              setNewItem({ kind: value, code: '', name: '', location_id: scopeLocationId || '', room: '' });
            }
          }}
          title="Crear un elemento sin salir del lienzo"
        >
          <option value="">＋ Nuevo…</option>
          <option value="switch">Switch</option>
          <option value="panel">Patch panel</option>
          <option value="outlet">Toma de pared</option>
          <option value="workstation">PC / portátil</option>
          <option value="printer">Impresora</option>
          <option value="ip_phone">Teléfono IP</option>
        </select>

        {selected && (
          <span className="swd-sel-hint">✦ {selectedPort?.node.title} {selectedPort?.port.label}: clic en otro puerto</span>
        )}
        {moveLink && (
          <span className="swd-sel-hint">
            ✦ Moviendo cable — clic en el puerto nuevo
            <button type="button" className="swd-link" onClick={cancelMoveLink}>cancelar</button>
          </span>
        )}

        <button
          type="button"
          className={`swd-btn${isRackView ? ' is-on' : ''}`}
          onClick={toggleView}
          title={isRackView ? 'Volver al lienzo de tarjetas' : 'Ver los racks con los equipos en su posición U'}
        >
          {isRackView ? '🗺 Lienzo' : '🗄 Armarios'}
        </button>

        {!isRackView && (
          <button type="button" className="swd-btn" onClick={autoOrganize} title="Reorganizar por armario y sede">
            ⌗ Organizar
          </button>
        )}
        <button type="button" className="swd-btn" onClick={fitView} title="Encajar la vista">⊞</button>
        <button type="button" className="swd-btn" onClick={toggleFullscreen} title={isFull ? 'Salir de pantalla completa' : 'Pantalla completa'}>
          {isFull ? '🗗' : '⛶'}
        </button>
        <button type="button" className="swd-btn" onClick={load} disabled={loading} title="Recargar">
          {loading ? '…' : '↻'}
        </button>
      </div>

      {/* Filtros por tipo */}
      <div className="cbd-filters">
        {ALL_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className={`cbd-chip${kindFilter.has(kind) ? ' is-on' : ''}`}
            style={{ '--chip': KIND_META[kind].color }}
            onClick={() => toggleKind(kind)}
            title={`Mostrar/ocultar ${KIND_META[kind].label}`}
            aria-pressed={kindFilter.has(kind)}
          >
            <KindIcon kind={kind} className="cbd-ico" aria-hidden="true" />
            {KIND_META[kind].label}
            <em>{stats[kind] || 0}</em>
          </button>
        ))}
        <button
          type="button"
          className={`swd-btn cbd-only-connected${onlyConnected ? ' is-on' : ''}`}
          onClick={toggleOnlyConnected}
          aria-pressed={onlyConnected}
          title={
            onlyConnected
              ? 'Ver todo (quitando el filtro de solo cableados)'
              : 'Solo los que tienen cable a otro extremo dentro del filtro actual'
          }
        >
          <EyeSlashIcon className="cbd-ico" aria-hidden="true" />
          Solo cableados
          {onlyConnected && nodesInFilter.length > 0 && (
            <em>{nodesInFilter.length - connectedInFilter.size}</em>
          )}
        </button>
        {isRackView && (
          <span className="cbd-rack-note">
            {rackNodes.length} equipos en rack
            {visibleNodes.length > rackNodes.length
              ? ` · ${visibleNodes.length - rackNodes.length} sin posición U (solo en el lienzo)`
              : ''}
          </span>
        )}
      </div>

      {error && (
        <div className="swd-banner is-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {notice && <div className="swd-banner is-notice">{notice}</div>}

      <div className={`swd-body${sideOpen ? '' : ' is-side-collapsed'}`}>
        <aside className="swd-side">
          <input
            className="swd-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar nodo (nombre, sede, armario)…"
          />
          <div className="swd-side-head">
            <span>{isRackView ? 'Equipos en rack' : 'Nodos'}</span>
            <span className="cbd-side-head-actions">
              {hiddenKeys.length > 0 && (
                <button type="button" className="cbd-head-btn" onClick={showAllNodes} title="Volver a mostrar todos los ocultos">
                  <EyeIcon className="cbd-ico is-kind" aria-hidden="true" />
                  Mostrar {hiddenKeys.length}
                </button>
              )}
              {listNodes.length > hiddenKeys.length && (
                <button type="button" className="cbd-head-btn" onClick={hideAllListed} title="Ocultar todos los elementos de la lista (luego puedes ir mostrando los que necesites)">
                  <EyeSlashIcon className="cbd-ico is-kind" aria-hidden="true" />
                  Ocultar todos
                </button>
              )}
            </span>
          </div>
          <div className="swd-side-list">
            {listGroups.map(([kind, rows]) => {
              const collapsed = collapsedGroups.has(kind);
              const anyHidden = rows.some((node) => hiddenSet.has(node.key));
              const anyVisible = rows.some((node) => !hiddenSet.has(node.key));
              const label = KIND_META[kind]?.label || kind;
              return (
                <div key={kind} className="cbd-side-group">
                  <div className="cbd-side-group-head">
                    <button
                      type="button"
                      className="cbd-side-group-toggle"
                      onClick={() => toggleGroup(kind)}
                      aria-expanded={!collapsed}
                      title={collapsed ? `Desplegar ${label}` : `Plegar ${label}`}
                    >
                      {collapsed
                        ? <ChevronRightIcon className="cbd-ico" aria-hidden="true" />
                        : <ChevronDownIcon className="cbd-ico" aria-hidden="true" />}
                      <KindIcon kind={kind} className="cbd-ico" style={{ color: KIND_META[kind]?.color }} aria-hidden="true" />
                      <span className="cbd-side-group-name">{label}</span>
                      <em>{rows.length}</em>
                    </button>
                    <span className="cbd-side-group-actions">
                      <button
                        type="button"
                        className="cbd-group-btn"
                        onClick={() => setGroupHidden(rows, false)}
                        disabled={!anyHidden}
                        title={`Mostrar todos los de ${label}`}
                        aria-label={`Mostrar todos los de ${label}`}
                      >
                        <EyeIcon className="cbd-ico is-kind" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="cbd-group-btn"
                        onClick={() => setGroupHidden(rows, true)}
                        disabled={!anyVisible}
                        title={`Ocultar todos los de ${label}`}
                        aria-label={`Ocultar todos los de ${label}`}
                      >
                        <EyeSlashIcon className="cbd-ico is-kind" aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                  {!collapsed && rows.map((node) => {
                    const isHidden = hiddenSet.has(node.key);
                    const byMode = isHiddenByMode(node);
                    return (
                      <div key={node.key} className={`swd-side-row${isHidden || byMode ? ' is-hidden' : ''}`}>
                        <button
                          type="button"
                          className="swd-eye"
                          title={
                            isHidden ? 'Mostrar en el lienzo'
                              : byMode ? 'Fuera por «Solo cableados»: pulsa para verlo'
                                : 'Ocultar del lienzo'
                          }
                          onClick={() => (byMode && !isHidden ? toggleOnlyConnected() : hideNode(node.key))}
                        >
                          {isHidden || byMode
                            ? <EyeSlashIcon className="cbd-ico" aria-hidden="true" />
                            : <EyeIcon className="cbd-ico" aria-hidden="true" />}
                        </button>
                        <button type="button" className="swd-side-info" onClick={() => focusNode(node)}>
                          <span className="swd-side-name">
                            <KindIcon kind={node.kind} className="cbd-ico is-kind" aria-hidden="true" />
                            {node.title}
                          </span>
                          <span className="swd-side-meta">
                            {node.stats?.connected || 0}/{node.stats?.ports || 0} puertos
                            {node.rack_name ? ` · ${node.rack_name}` : ''}
                            {!node.rack_name && node.location_name ? ` · ${node.location_name}` : ''}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {!listNodes.length && (
              <div className="swd-empty">{loading ? 'Cargando…' : 'Sin nodos con esos filtros'}</div>
            )}
          </div>

          {selectedPort && (
            <div className="cbd-chain">
              <div className="cbd-chain-head">
                <span className="cbd-chain-title">
                  <LinkIcon className="cbd-ico" aria-hidden="true" />
                  Cadena
                </span>
                <button type="button" className="swd-link" onClick={() => { setChainFocus(null); setSelected(null); }}>cerrar</button>
              </div>
              <div className="cbd-chain-node">
                <strong>{selectedPort.port.label}</strong>
                <span>{selectedPort.node.title}</span>
              </div>
              {chainLoading && <div className="cbd-chain-row">Siguiendo el cable…</div>}
              {!chainLoading && !(chain || []).length && (
                <div className="cbd-chain-row">Sin cables: selecciona otro puerto libre para conectar.</div>
              )}
              {(chain || []).map((link, index) => (
                <div key={`${link.kind}-${link.linked_id}-${index}`} className={`cbd-chain-row is-${link.kind}`}>
                  <span className="cbd-chain-dot" style={{ background: colorHex(link.cable_color) }} />
                  <span>
                    {link.kind === 'outlet_through'
                      ? '↔ pasa por la toma'
                      : (
                        <>
                          {link.kind === 'backbone' ? '🪢' : link.kind === 'wall_run' ? '🧱' : '🔌'}{' '}
                          {truncate(link.to?.element_name || link.to?.label || '—', 26)}
                          {link.to?.label ? ` · ${truncate(link.to.label, 14)}` : ''}
                        </>
                      )}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="swd-legend">
            <div className="swd-legend-row"><i className="cbd-dot is-free" /> Puerto libre</div>
            <div className="swd-legend-row"><i className="cbd-dot is-busy" /> Con cable</div>
            <div className="swd-legend-row"><i className="cbd-dot is-external" /> Cable fuera del ámbito</div>
            <div className="swd-legend-row"><i className="cbd-dot is-off" /> Inactivo</div>
            <div className="swd-legend-tip">
              Clic en un puerto libre → clic en otro → eliges el cable.
              <br />
              Clic en un puerto ocupado → ilumina todo su camino y lo abre en «Cadena».
              <br />
              Arrastra una tarjeta o su armario. Rueda = zoom.
            </div>
          </div>
        </aside>

        <div className="swd-canvas-wrap" ref={canvasRef}>
          <svg
            ref={svgRef}
            className="swd-svg"
            onMouseDown={onCanvasDown}
            onMouseMove={(e) => {
              if (!selected) return;
              const pt = worldFromEvent(e);
              if (pt) setGhost(pt);
            }}
            onMouseLeave={() => setGhost(null)}
          >
            <defs>
              <pattern id="cbd-grid" width="26" height="26" patternUnits="userSpaceOnUse">
                <circle cx="1.2" cy="1.2" r="1.1" fill="var(--swd-grid-dot)" />
              </pattern>
            </defs>
            <rect className="swd-bg" x="0" y="0" width="100%" height="100%" fill="url(#cbd-grid)" />

            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {/* Vista de armarios: cajas de rack y equipos en su U */}
              {isRackView && rackLayout.boxes.map((rackBox) => (
                <g key={rackBox.key} className="cbd-rack">
                  <rect
                    className="cbd-rack-frame"
                    x={rackBox.x}
                    y={rackBox.y}
                    width={rackBox.width}
                    height={rackBox.height}
                    rx={12}
                  />
                  <text className="cbd-rack-title" x={rackBox.x + 14} y={rackBox.y + 21}>
                    {truncate(rackBox.title, 44)}
                  </text>
                  {Array.from({ length: rackBox.heightU }, (_, i) => {
                    const u = rackBox.heightU - i;
                    const y = rackBox.y + RACK_HEADER + i * U_H;
                    return (
                      <g key={`slot-${rackBox.key}-${u}`}>
                        <line
                          className="cbd-rack-slot"
                          x1={rackBox.x + 6}
                          x2={rackBox.x + rackBox.width - 6}
                          y1={y}
                          y2={y}
                        />
                        {u % 5 === 0 && (
                          <text className="cbd-rack-u" x={rackBox.x + rackBox.width - 6} y={y + 11} textAnchor="end">
                            {u}U
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              ))}

              {isRackView && rackNodes.map((node) => {
                const pos = effectivePositions[node.key];
                const geo = effectiveGeos.get(node.key);
                if (!pos || !geo) return null;
                const meta = KIND_META[node.kind] || { color: '#64748b' };
                const inPath = pathNodes.has(node.key);
                const dimmed = hasPath && !inPath;
                return (
                  <g
                    key={`unit-${node.key}`}
                    className={`cbd-rack-unit${inPath ? ' is-path' : ''}${dimmed ? ' is-dim' : ''}`}
                    transform={`translate(${pos.x},${pos.y})`}
                    style={{ '--kind': meta.color }}
                  >
                    <rect className="cbd-rack-unit-rect" width={geo.width} height={geo.height} rx={4} />
                    <rect className="cbd-rack-unit-bar" width={4} height={geo.height} rx={2} />
                    <text className="cbd-rack-unit-name" x={10} y={geo.height / 2 + 4}>
                      {truncate(node.title, 16)}
                    </text>
                    <title>{`${node.title} · U${node.position_u} · ${node.size_u || 1}U`}</title>
                  </g>
                );
              })}

              {/* Cajas de armario / sede (solo lienzo) */}
              {!isRackView && groups.map((group) => {
                const box = boxFromNodes(group.nodes, positions);
                if (!box) return null;
                return (
                  <g key={`box-${group.key}`} className="cbd-group">
                    <rect
                      x={box.x}
                      y={box.y}
                      width={box.width}
                      height={box.height}
                      rx={16}
                      onMouseDown={(e) => startGroupDrag(e, group)}
                    >
                      <title>Arrastrar el {group.title} entero</title>
                    </rect>
                    <text x={box.x + 16} y={box.y + 20} className="cbd-group-title">
                      {truncate(group.title, 42)}
                    </text>
                  </g>
                );
              })}

              {/* Zonas de clic de los cables */}
              {visibleEdges.map((edge) => {
                const geo = edgeGeometry(edge);
                if (!geo) return null;
                return (
                  <path
                    key={`hit-${edge.key}`}
                    className="swd-edge-hit"
                    d={geo.d}
                    onClick={() => {
                      const hit = portIndex.get(edge.a);
                      if (hit) openEdgesModal(hit.node, hit.port);
                    }}
                  >
                    <title>{`${EDGE_META[edge.kind]?.label || edge.kind}${edge.cable_type ? ` · ${edge.cable_type}` : ''}`}</title>
                  </path>
                );
              })}

              {/* Cables */}
              {visibleEdges.map((edge) => {
                const geo = edgeGeometry(edge);
                if (!geo) return null;
                const style = EDGE_META[edge.kind] || EDGE_META.port_connection;
                const color = colorHex(edge.cable_color);
                const hovered = highlightEdges.has(edge.key);
                const inPath = activePath.edges.has(edge.key);
                const dimmed = hasPath && !inPath;
                return (
                  <g key={`line-${edge.key}`} className={`swd-edge${hovered ? ' is-hover' : ''} cbd-edge is-${edge.kind}${inPath ? ' is-path' : ''}${dimmed ? ' is-dim' : ''}`}>
                    <path
                      className="swd-edge-line"
                      d={geo.d}
                      stroke={edge.kind === 'wall_run' ? 'var(--cbd-wall)' : edge.kind === 'backbone' ? 'var(--cbd-backbone)' : color}
                      strokeWidth={style.width}
                      strokeDasharray={edge.kind === 'wall_run' ? '2 4' : isFiber(edge.cable_type) ? '7 4' : undefined}
                    />
                    {(edge.kind === 'backbone' || hovered) && (
                      <circle cx={geo.mid.x} cy={geo.mid.y} r={5} fill={edge.kind === 'backbone' ? 'var(--cbd-backbone)' : color} />
                    )}
                  </g>
                );
              })}

              {/* Tarjetas (solo lienzo) */}
              {!isRackView && visibleNodes.map((node) => {
                const geo = geoByKey.get(node.key);
                const pos = positions[node.key];
                if (!geo || !pos) return null;
                const meta = KIND_META[node.kind] || { color: '#64748b', card: 'compact' };
                const ports = node.ports || [];
                const connected = ports.filter((p) => p.edges?.length || p.external).length;

                return (
                  <g
                    key={node.key}
                    className={`cbd-card${geo.compact ? ' is-compact' : ''}${node.install_state === 'planned' ? ' is-design' : ''}${pathNodes.has(node.key) ? ' is-path' : ''}${hasPath && !pathNodes.has(node.key) ? ' is-dim' : ''}`}
                    transform={`translate(${pos.x},${pos.y})`}
                    style={{ '--kind': meta.color }}
                  >
                    <title>{`${node.title}\n${node.subtitle || ''}\n${connected}/${ports.length} puertos cableados${node.kind === 'switch' ? '\nDoble clic: disposición de puertos (filas y numeración)' : ''}`}</title>
                    <rect
                      className="swd-card-rect cbd-card-rect"
                      width={geo.width}
                      height={geo.height}
                      rx={9}
                      onMouseDown={(e) => startDrag(e, node)}
                      onDoubleClick={(e) => { e.stopPropagation(); openLayoutModal(node); }}
                    />
                    {/* Columna de información a la izquierda, igual que en el
                        Diseñador de switches: deja libres las franjas de arriba
                        y abajo de los puertos, que es por donde salen los cables. */}
                    <path
                      className="cbd-card-info"
                      d={`M ${geo.compact ? geo.padX - 8 : geo.infoW} 0 L 9 0 Q 0 0 0 9 L 0 ${geo.height - 9} Q 0 ${geo.height} 9 ${geo.height} L ${geo.compact ? geo.padX - 8 : geo.infoW} ${geo.height} Z`}
                      onMouseDown={(e) => startDrag(e, node)}
                      onDoubleClick={(e) => { e.stopPropagation(); openLayoutModal(node); }}
                    />
                    <g
                      className="cbd-card-text"
                      onMouseDown={(e) => startDrag(e, node)}
                      onDoubleClick={(e) => { e.stopPropagation(); openLayoutModal(node); }}
                    >
                      <text x={11} y={geo.compact ? 14 : geo.height / 2 - 9} className="cbd-card-name">
                        {truncate(node.title, geo.compact ? 13 : 15)}
                      </text>
                      <text x={11} y={geo.compact ? 25 : geo.height / 2 + 5} className="swd-card-sub">
                        {truncate(node.subtitle || meta.label, geo.compact ? 11 : 16)}
                      </text>
                      {!geo.compact && (
                        <text x={12} y={geo.height / 2 + 17} className="swd-card-sub">
                          {truncate(`${connected}/${ports.length} cableados${node.install_state === 'planned' ? ' · DISEÑO' : ''}`, 16)}
                        </text>
                      )}
                    </g>
                    {!ports.length && (
                      <text x={geo.width / 2} y={geo.height / 2 + 3} className="swd-card-empty" textAnchor="middle">
                        Sin puertos
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Capa de puertos (encima de los cables) */}
              {effectiveNodes.map((node) => {
                const geo = effectiveGeos.get(node.key);
                const pos = effectivePositions[node.key];
                if (!geo || !pos) return null;
                const pw = geo.portW || PORT_W;
                const ph = geo.portH || PORT_H;
                const isRack = geo.mode === 'rack';
                return (
                  <g key={`ports-${node.key}`} transform={`translate(${pos.x},${pos.y})`}>
                    {(node.ports || []).map((port, index) => {
                      const off = offsetOf(geo, index);
                      const portLeads = leads.get(port.key) || [];
                      const isSel = selected?.portKey === port.key;
                      const state = portState(port);
                      const inPath = activePath.ports.has(port.key);
                      const dimmed = hasPath && !inPath;
                      return (
                        <g key={port.key}>
                          {portLeads.map((lead, li) => (
                            <line
                              key={`lead-${li}`}
                              className={`swd-lead${inPath ? ' is-path' : ''}${dimmed ? ' is-dim' : ''}`}
                              x1={off.x}
                              y1={lead.side === -1 ? off.y - ph / 2 : off.y + ph / 2}
                              x2={off.x}
                              y2={lead.side === -1 ? 0 : geo.height}
                              stroke={lead.kind === 'wall_run' ? 'var(--cbd-wall)' : colorHex(lead.color)}
                            />
                          ))}
                          <g
                            className={`cbd-port is-${state}${isRack ? ' is-rack' : ''}${isSel ? ' is-selected' : ''}${hoverPortKey === port.key ? ' is-hover' : ''}${inPath ? ' is-path' : ''}${dimmed ? ' is-dim' : ''}`}
                            transform={`translate(${off.x - pw / 2},${off.y - ph / 2})`}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => handlePortClick(e, node, port)}
                            onDoubleClick={(e) => { e.stopPropagation(); revealPath(node, port); }}
                            onMouseEnter={() => setHoverPortKey(port.key)}
                            onMouseLeave={() => setHoverPortKey((cur) => (cur === port.key ? null : cur))}
                          >
                            <rect width={pw} height={ph} rx={isRack ? 1.5 : 2.5} />
                            {port.meta?.poe && <circle className="swd-port-poe" cx={pw - 2.5} cy={2.5} r={2.2} />}
                            <text x={pw / 2} y={ph / 2 + (isRack ? 2 : 2.5)} textAnchor="middle" className="swd-port-num">
                              {port.number ?? index + 1}
                            </text>
                            <title>{portTip(port, node)}</title>
                          </g>
                        </g>
                      );
                    })}
                  </g>
                );
              })}

              {ghostLine && <path className="swd-edge-ghost" d={ghostLine.d} stroke="var(--swd-accent)" />}
            </g>
          </svg>

          {!effectiveNodes.length && !loading && (
            <div className="swd-canvas-empty">
              <div className="swd-canvas-empty-ic">{isRackView ? '🗄' : '🧵'}</div>
              {isRackView
                ? 'No hay equipos con posición U en este ámbito.'
                : 'No hay nada que cablear en este ámbito.'}
              <div className="swd-canvas-empty-sub">
                {isRackView
                  ? 'Solo se colocan en el frontal los equipos con U asignada. Prueba con toda la empresa o con otro armario.'
                  : 'Elige una sede u armarios, crea un elemento con «＋ Nuevo» o cambia los filtros.'}
              </div>
              <div className="swd-canvas-empty-actions">
                {isRackView && scopeMode !== 'company' && (
                  <button type="button" className="swd-btn" onClick={() => setScopeMode('company')}>
                    Ver todos los armarios de la empresa
                  </button>
                )}
                <button type="button" className="swd-btn" onClick={toggleView}>
                  {isRackView ? '🗺 Ir al lienzo' : '🗄 Ir a armarios'}
                </button>
              </div>
            </div>
          )}

          <div className="swd-zoom">
            <button type="button" onClick={() => { userAdjustedRef.current = true; setZoom((z) => clamp(z * 1.2, ZMIN, ZMAX)); }}>＋</button>
            <button type="button" onClick={() => { userAdjustedRef.current = false; fitViewRef.current?.(); }} title="Reencuadrar">
              {Math.round(zoom * 100)}%
            </button>
            <button type="button" onClick={() => { userAdjustedRef.current = true; setZoom((z) => clamp(z / 1.2, ZMIN, ZMAX)); }}>－</button>
          </div>
        </div>
      </div>

      {/* ── Modal: nuevo cable ── */}
      {connectModal && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConnectModal(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">
              {connectModal.kind === 'backbone' ? '🪢 Enlace entre armarios'
                : connectModal.kind === 'wall_run' ? '🧱 Cable de pared (toma ↔ panel)'
                  : '🔌 Nuevo latiguillo'}
            </h3>
            <div className="swd-endpoints">
              {[connectModal.a, connectModal.b].map((end, i) => (
                <React.Fragment key={i}>
                  {i === 1 && <div className="swd-endpoint-arrow">↔</div>}
                  <div className="swd-endpoint">
                    <span className="swd-endpoint-tag">{KIND_META[end.node.kind]?.label || 'Extremo'}</span>
                    <strong>{end.node.title}</strong>
                    <span className="swd-endpoint-port">
                      {end.port.label}
                      {end.port.meta?.side === 'panel' ? ' · armario' : end.port.meta?.side === 'room' ? ' · escritorio' : ''}
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {connectModal.kind === 'backbone' && (
              <p className="swd-modal-hint">
                {connectModal.sameRack
                  ? '⚠️ Las dos bocas son del MISMO armario: esto no es un enlace entre armarios. Para unir dos armarios, elige una boca del panel del otro armario.'
                  : 'Enlace permanente entre dos armarios. La boca sigue libre para su latiguillo al switch; por defecto se propone fibra.'}
              </p>
            )}

            {connectModal.kind === 'wall_run' ? (
              <label className="cbd-field">
                Etiqueta / notas
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Ej: cable en canaleta, planta 1…"
                />
              </label>
            ) : (
              <div className="swd-form-grid">
                <label>
                  Tipo de cable
                  <select value={form.cable_type} onChange={(e) => setForm((f) => ({ ...f, cable_type: e.target.value }))}>
                    {CABLE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </label>
                <label>
                  Color
                  <select value={form.cable_color} onChange={(e) => setForm((f) => ({ ...f, cable_color: e.target.value }))}>
                    {CABLE_COLORS.map((color) => <option key={color.value} value={color.value}>{color.label}</option>)}
                  </select>
                </label>
                <label>
                  Longitud (m)
                  <input
                    type="number" min="0" step="0.5" value={form.length_m}
                    onChange={(e) => setForm((f) => ({ ...f, length_m: e.target.value }))}
                    placeholder="opcional"
                  />
                </label>
                <label>
                  Etiqueta / notas
                  <input
                    type="text" value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Ej: trunk VLAN 13…"
                  />
                </label>
              </div>
            )}

            <div className="swd-modal-actions">
              <button type="button" className="swd-btn is-ghost" onClick={() => setConnectModal(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="swd-btn is-primary" onClick={doConnect} disabled={saving}>
                {saving ? 'Guardando…' : '🔌 Conectar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: cables de un puerto ── */}
      {edgesModal && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEdgesModal(null); }}>
          <div className="swd-modal cbd-modal-wide">
            <h3 className="swd-modal-title">
              🔌 {edgesModal.node.title} · {edgesModal.port.label}
            </h3>
            {!edgesModal.edges.length && <p className="swd-modal-hint">Ese puerto no tiene cables.</p>}
            {edgesModal.edges.map((edge) => {
              const otherKey = edge.a === edgesModal.port.key ? edge.b : edge.a;
              const other = portIndex.get(otherKey);
              const style = EDGE_META[edge.kind] || EDGE_META.port_connection;
              return (
                <div key={edge.key} className="cbd-edge-row">
                  <div className="cbd-edge-head">
                    <span className={`cbd-edge-tag is-${edge.kind}`}>{style.label}</span>
                    <button
                      type="button"
                      className="swd-link"
                      title="Cambiar este extremo a otro puerto sin desmontar el cable"
                      onClick={() => startMoveLink(edgesModal.node, edgesModal.port, edge)}
                    >
                      mover
                    </button>
                    <button type="button" className="swd-link" onClick={() => openEdgeForm(edge)}>editar</button>
                    <button type="button" className="swd-link is-danger" onClick={() => doDeleteEdge(edge)} disabled={saving}>
                      desconectar
                    </button>
                  </div>
                  <div className="cbd-edge-body">
                    <strong>{other?.node.title || 'Fuera del ámbito'}</strong>
                    <span>{other?.port.label || '—'}</span>
                    {edge.cable_type && <em>{edge.cable_type}{edge.cable_color ? ` · ${edge.cable_color}` : ''}</em>}
                    {edge.length_m != null && <em>{edge.length_m} m</em>}
                    {edge.notes && <em>{edge.notes}</em>}
                  </div>
                </div>
              );
            })}

            {canAddCable(edgesModal.port, edgesModal.edges.length) && (
              <p className="swd-modal-hint">
                {edgesModal.port.type === 'panel_port'
                  ? 'Una boca de panel admite a la vez latiguillo, cable de pared y enlace entre armarios.'
                  : 'Una toma de pared lleva dos cables: el de pared (por detrás, hasta el panel) y el latiguillo del equipo.'}
              </p>
            )}

            <div className="swd-modal-actions">
              {canAddCable(edgesModal.port, edgesModal.edges.length) && (
                <button type="button" className="swd-btn is-primary" onClick={() => startCableFrom(edgesModal)}>
                  ➕ Añadir otro cable
                </button>
              )}
              <button type="button" className="swd-btn is-ghost" onClick={() => setEdgesModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: editar cable ── */}
      {edgesModal && edgesModal.editing && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEdgesModal(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">✏️ Editar cable</h3>
            <div className="swd-form-grid">
              <label>
                Tipo de cable
                <select value={form.cable_type} onChange={(e) => setForm((f) => ({ ...f, cable_type: e.target.value }))}>
                  {CABLE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </label>
              <label>
                Color
                <select value={form.cable_color} onChange={(e) => setForm((f) => ({ ...f, cable_color: e.target.value }))}>
                  {CABLE_COLORS.map((color) => <option key={color.value} value={color.value}>{color.label}</option>)}
                </select>
              </label>
              <label>
                Longitud (m)
                <input
                  type="number" min="0" step="0.5" value={form.length_m}
                  onChange={(e) => setForm((f) => ({ ...f, length_m: e.target.value }))}
                />
              </label>
              <label>
                Etiqueta / notas
                <input type="text" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </label>
            </div>
            <div className="swd-modal-actions">
              <button type="button" className="swd-btn is-ghost" onClick={() => setEdgesModal(null)}>Cancelar</button>
              <button type="button" className="swd-btn is-primary" onClick={() => doUpdateEdge(edgesModal.editing)} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: alta rápida ── */}
      {newItem && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setNewItem(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">
              <KindIcon kind={newItem.kind} className="cbd-ico" aria-hidden="true" /> Nuevo {KIND_META[newItem.kind]?.label?.toLowerCase()}
            </h3>
            <div className="swd-form-grid">
              <label>
                {newItem.kind === 'outlet' ? 'Código' : 'Nombre'}
                <input
                  type="text" value={newItem.name || newItem.code}
                  onChange={(e) => setNewItem((f) => ({ ...f, name: e.target.value, code: e.target.value }))}
                  placeholder={newItem.kind === 'outlet' ? 'T-24' : 'PC-RECEPCION'}
                />
              </label>
              <label>
                Sede / ubicación
                <select value={newItem.location_id} onChange={(e) => setNewItem((f) => ({ ...f, location_id: e.target.value }))}>
                  <option value="">— Sin ubicación —</option>
                  {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </label>
              <label>
                Sala / despacho
                <input type="text" value={newItem.room} onChange={(e) => setNewItem((f) => ({ ...f, room: e.target.value }))} />
              </label>
            </div>
            <p className="swd-modal-hint">
              Se crea en su inventario de siempre. Después se cablea aquí mismo con dos clics.
            </p>
            <div className="swd-modal-actions">
              <button type="button" className="swd-btn is-ghost" onClick={() => setNewItem(null)} disabled={saving}>Cancelar</button>
              <button type="button" className="swd-btn is-primary" onClick={createNewItem} disabled={saving}>
                {saving ? 'Creando…' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: disposición de puertos (idéntico al Diseñador de switches) ── */}
      {layoutModal && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setLayoutModal(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">⚙️ {layoutModal.node.title}</h3>
            <div className="swd-form-grid">
              <label>
                Puertos por fila
                <input
                  type="text"
                  value={layoutModal.rowsText}
                  onChange={(e) => setLayoutModal((c) => ({ ...c, rowsText: e.target.value }))}
                  placeholder="auto"
                />
                <span className="swd-field-hint">
                  Ej: <code>12, 12</code> → 12 arriba y el resto abajo. Vacío = automático.
                </span>
              </label>
              <label>
                Numeración
                <select
                  value={layoutModal.numbering}
                  onChange={(e) => setLayoutModal((c) => ({ ...c, numbering: e.target.value }))}
                >
                  <option value="rows">Por filas (1-12 · 13-24)</option>
                  <option value="alternate">Intercalada (1, 3, 5… · 2, 4, 6…)</option>
                </select>
                <span className="swd-field-hint">
                  Se guarda en el mismo sitio que el Diseñador de switches: los dos cambian a la vez.
                </span>
              </label>
            </div>
            <div className="swd-modal-actions">
              <button type="button" className="swd-btn is-ghost" onClick={() => setLayoutModal(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="swd-btn is-primary" onClick={saveLayoutModal} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Alta de switch / patch panel (formulario de siempre) ── */}
      {elementForm && (
        <PortElementForm
          elementType={elementForm}
          item={null}
          companyId={companyId}
          initialMode="create"
          onClose={() => setElementForm(null)}
          onSave={async () => {
            setElementForm(null);
            setNotice(`${elementForm === 'switch' ? 'Switch' : 'Patch panel'} creado: ya está en el lienzo`);
            await load();
          }}
        />
      )}
    </div>
  );
}

export default CablingDesigner;
